// src/components/annotation/utils/drawing.ts

import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import { Tag } from '../../../types/models/tag'
import { MousePosition } from '../hooks/useImageAnnotation'
import { getContrastYIQ } from '../../../utils'
import { pointsArrayToStructured, isAxisAligned, getMinMaxFromPoints } from '../../../utils/helpers'

// ============================================================================
// ORIENTED BOUNDING BOX DRAWING UTILITIES
// ============================================================================

/**
 * Draw an oriented bounding box using strokePath for full rotation support
 * Points are expected to be in pixel coordinates
 */
export const drawBoundingBoxFromPoints = (
  ctx: CanvasRenderingContext2D,
  points: number[],
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  strokeStyle: string,
  fillStyle?: string,
  lineWidth = 2
) => {
  if (points.length !== 8) return

  // Convert pixel coordinates to canvas coordinates
  const scaleX = canvasWidth / imageWidth
  const scaleY = canvasHeight / imageHeight

  const scaledPoints = [
    points[0] * scaleX, // top-left x
    points[1] * scaleY, // top-left y
    points[2] * scaleX, // top-right x
    points[3] * scaleY, // top-right y
    points[4] * scaleX, // bottom-right x
    points[5] * scaleY, // bottom-right y
    points[6] * scaleX, // bottom-left x
    points[7] * scaleY, // bottom-left y
  ]

  ctx.beginPath()
  ctx.moveTo(scaledPoints[0], scaledPoints[1]) // top-left
  ctx.lineTo(scaledPoints[2], scaledPoints[3]) // top-right
  ctx.lineTo(scaledPoints[4], scaledPoints[5]) // bottom-right
  ctx.lineTo(scaledPoints[6], scaledPoints[7]) // bottom-left
  ctx.closePath()

  // Fill if fillStyle provided
  if (fillStyle) {
    ctx.fillStyle = fillStyle
    ctx.fill()
  }

  // Stroke
  ctx.strokeStyle = strokeStyle
  ctx.lineWidth = lineWidth
  ctx.stroke()
}

/**
 * Draw resize handles for oriented bounding box
 * Points are expected to be in pixel coordinates
 */
export const drawOrientedResizeHandles = (
  ctx: CanvasRenderingContext2D,
  points: number[],
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  handleColor: string,
  handleSize: number,
  zoomLevel: number,
  cssScale = 1
) => {
  if (points.length !== 8) return

  // Convert pixel coordinates to canvas coordinates
  const scaleX = canvasWidth / imageWidth
  const scaleY = canvasHeight / imageHeight

  const canvasPoints = [
    points[0] * scaleX, // top-left x
    points[1] * scaleY, // top-left y
    points[2] * scaleX, // top-right x
    points[3] * scaleY, // top-right y
    points[4] * scaleX, // bottom-right x
    points[5] * scaleY, // bottom-right y
    points[6] * scaleX, // bottom-left x
    points[7] * scaleY, // bottom-left y
  ]

  const bbox = pointsArrayToStructured(canvasPoints)

  ctx.strokeStyle = handleColor
  ctx.fillStyle = handleColor
  // Keep thickness consistent with bbox outline and zoom
  // const borderWidth = Math.max(1, 2 / zoomLevel)
  const borderWidth = Math.max(0.25, 2 / (zoomLevel * cssScale))
  // Stroke scales with zoom; keep thin at high zoom
  // Match border thickness for handle strokes
  const handleLineWidth = borderWidth
  const haloLineWidth = Math.max(0.3, handleLineWidth + 1 / (zoomLevel * cssScale)) // subtle halo
  const haloColor = getContrastYIQ(handleColor) === 'white' ? '#ffffff' : '#000000'
  ctx.lineWidth = handleLineWidth
  ctx.lineCap = 'round'

  // Corner handles as 90-degree arcs inside the box
  // Use the provided handleSize directly so OBB matches BBox scaling behavior
  const radius = Math.max(0.5, handleSize)
  // Consistent insets
  const edgeInset = borderWidth + handleLineWidth * 0.25
  const centerOffset = radius + borderWidth + handleLineWidth * 0.25
  // Corner arcs aligned to OBB orientation using local axes
  const cornerU = {
    x: bbox.topRight.x - bbox.topLeft.x,
    y: bbox.topRight.y - bbox.topLeft.y,
  }
  const cornerV = {
    x: bbox.bottomLeft.x - bbox.topLeft.x,
    y: bbox.bottomLeft.y - bbox.topLeft.y,
  }
  const ulenLocal = Math.hypot(cornerU.x, cornerU.y) || 1
  const vlenLocal = Math.hypot(cornerV.x, cornerV.y) || 1
  const ux = { x: cornerU.x / ulenLocal, y: cornerU.y / ulenLocal }
  const vy = { x: cornerV.x / vlenLocal, y: cornerV.y / vlenLocal }

  const drawCornerArcOriented = (
    corner: { x: number; y: number },
    xAxis: { x: number; y: number },
    yAxis: { x: number; y: number },
    start: number,
    end: number
  ) => {
    ctx.save()
    // Move origin to corner and rotate axes to local orientation
    ctx.translate(corner.x, corner.y)
    ctx.transform(xAxis.x, xAxis.y, yAxis.x, yAxis.y, 0, 0)
    // Halo
    ctx.beginPath()
    ctx.strokeStyle = haloColor
    ctx.lineWidth = haloLineWidth
    ctx.arc(centerOffset, centerOffset, radius, start, end)
    ctx.stroke()
    // Foreground
    ctx.beginPath()
    ctx.strokeStyle = handleColor
    ctx.lineWidth = handleLineWidth
    ctx.arc(centerOffset, centerOffset, radius, start, end)
    ctx.stroke()
    ctx.restore()
  }
  // Orient axes so +x and +y point inward; then always draw 180->270 in local space
  drawCornerArcOriented(bbox.topLeft, ux, vy, Math.PI, 1.5 * Math.PI)
  drawCornerArcOriented(bbox.topRight, { x: -ux.x, y: -ux.y }, vy, Math.PI, 1.5 * Math.PI)
  drawCornerArcOriented(
    bbox.bottomRight,
    { x: -ux.x, y: -ux.y },
    { x: -vy.x, y: -vy.y },
    Math.PI,
    1.5 * Math.PI
  )
  drawCornerArcOriented(bbox.bottomLeft, ux, { x: -vy.x, y: -vy.y }, Math.PI, 1.5 * Math.PI)

  // Edge handles as centered segments inside the bbox: exactly 20% of the edge length
  const drawEdgeSegment = (ax: number, ay: number, bx: number, by: number) => {
    const dx = bx - ax
    const dy = by - ay
    const length = Math.hypot(dx, dy)
    if (length === 0) return
    const segLength = length * 0.2
    const mx = (ax + bx) / 2
    const my = (ay + by) / 2
    const ux = dx / length
    const uy = dy / length
    const hx = (segLength / 2) * ux
    const hy = (segLength / 2) * uy
    // Inset the segment slightly inside the box along the inward normal
    const centerX = (bbox.topLeft.x + bbox.topRight.x + bbox.bottomRight.x + bbox.bottomLeft.x) / 4
    const centerY = (bbox.topLeft.y + bbox.topRight.y + bbox.bottomRight.y + bbox.bottomLeft.y) / 4
    // Normal vector (rotate tangent by +90deg)
    let nx = -uy
    let ny = ux
    // Choose inward direction by dot with center vector
    const dot = (centerX - mx) * nx + (centerY - my) * ny
    if (dot < 0) {
      nx = -nx
      ny = -ny
    }
    const offX = nx * edgeInset
    const offY = ny * edgeInset

    const x1 = mx - hx + offX
    const y1 = my - hy + offY
    const x2 = mx + hx + offX
    const y2 = my + hy + offY

    // Halo underlay
    ctx.save()
    ctx.beginPath()
    ctx.strokeStyle = haloColor
    ctx.lineWidth = haloLineWidth
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
    // Foreground stroke
    ctx.beginPath()
    ctx.strokeStyle = handleColor
    ctx.lineWidth = handleLineWidth
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
    ctx.restore()
  }

  drawEdgeSegment(bbox.topLeft.x, bbox.topLeft.y, bbox.topRight.x, bbox.topRight.y)
  drawEdgeSegment(bbox.topRight.x, bbox.topRight.y, bbox.bottomRight.x, bbox.bottomRight.y)
  drawEdgeSegment(bbox.bottomRight.x, bbox.bottomRight.y, bbox.bottomLeft.x, bbox.bottomLeft.y)
  drawEdgeSegment(bbox.bottomLeft.x, bbox.bottomLeft.y, bbox.topLeft.x, bbox.topLeft.y)

  // Rotate handle: small circle offset from top edge inward along its normal
  {
    const ax = bbox.topLeft.x
    const ay = bbox.topLeft.y
    const bx = bbox.topRight.x
    const by = bbox.topRight.y
    const mx = (ax + bx) / 2
    const my = (ay + by) / 2
    const dx = bx - ax
    const dy = by - ay
    const len = Math.hypot(dx, dy) || 1
    let nx = -dy / len
    let ny = dx / len
    const cx = (bbox.topLeft.x + bbox.topRight.x + bbox.bottomRight.x + bbox.bottomLeft.x) / 4
    const cy = (bbox.topLeft.y + bbox.topRight.y + bbox.bottomRight.y + bbox.bottomLeft.y) / 4
    const dot = (mx - cx) * nx + (my - cy) * ny
    // Choose inward direction toward the center
    if (dot > 0) {
      nx = -nx
      ny = -ny
    }
    const rotateOffset = radius + edgeInset
    const rx = mx + nx * rotateOffset
    const ry = my + ny * rotateOffset
    const rotateRadius = Math.max(0.5, handleSize * 0.8)

    // Halo
    ctx.beginPath()
    ctx.strokeStyle = haloColor
    ctx.lineWidth = haloLineWidth
    ctx.arc(rx, ry, rotateRadius, 0, Math.PI * 2)
    ctx.stroke()
    // Foreground
    ctx.beginPath()
    ctx.strokeStyle = handleColor
    ctx.lineWidth = handleLineWidth
    ctx.arc(rx, ry, rotateRadius, 0, Math.PI * 2)
    ctx.stroke()

    // Subtle tick line from edge midpoint to rotate handle
    ctx.beginPath()
    ctx.strokeStyle = haloColor
    ctx.lineWidth = haloLineWidth
    ctx.moveTo(mx, my)
    ctx.lineTo(rx, ry)
    ctx.stroke()
    ctx.beginPath()
    ctx.strokeStyle = handleColor
    ctx.lineWidth = handleLineWidth
    ctx.moveTo(mx, my)
    ctx.lineTo(rx, ry)
    ctx.stroke()
  }

  // Start-corner border highlight: recolor a short L-shape of the actual border at top-left
  const highlightLen = Math.max(1, 1 / zoomLevel)
  const highlightColor = getContrastYIQ(handleColor)
  const start = bbox.topLeft
  ctx.save()
  ctx.strokeStyle = highlightColor
  ctx.lineWidth = (borderWidth * 2) / zoomLevel
  ctx.lineCap = 'round'
  // Along top edge direction (ux)
  ctx.beginPath()
  ctx.moveTo(start.x, start.y)
  ctx.lineTo(start.x + ux.x * highlightLen, start.y + ux.y * highlightLen)
  ctx.stroke()
  // Along left edge direction (vy)
  ctx.beginPath()
  ctx.moveTo(start.x, start.y)
  ctx.lineTo(start.x + vy.x * highlightLen, start.y + vy.y * highlightLen)
  ctx.stroke()
  ctx.restore()
}

/**
 * Check if a point is near any of the bounding box handles
 * Points and mouse coordinates are in pixel coordinates
 * handleSizeCanvas is the handle size in canvas coordinates
 */
export const getOrientedResizeHandle = (
  points: number[],
  mouseX: number,
  mouseY: number,
  handleSizeCanvas: number,
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number
): string | null => {
  if (points.length !== 8) return null

  // Convert pixel coordinates to canvas coordinates for consistent comparison
  const scaleX = canvasWidth / imageWidth
  const scaleY = canvasHeight / imageHeight
  const canvasPoints = [
    points[0] * scaleX, // top-left x
    points[1] * scaleY, // top-left y
    points[2] * scaleX, // top-right x
    points[3] * scaleY, // top-right y
    points[4] * scaleX, // bottom-right x
    points[5] * scaleY, // bottom-right y
    points[6] * scaleX, // bottom-left x
    points[7] * scaleY, // bottom-left y
  ]

  // Convert mouse coordinates to canvas space
  const canvasMouseX = mouseX * scaleX
  const canvasMouseY = mouseY * scaleY

  // Debug: log core values
  // Note: Using debug to allow filtering in devtools
  console.log('[ResizeDetect] scales', {
    scaleX,
    scaleY,
    canvasWidth,
    canvasHeight,
    imageWidth,
    imageHeight,
  })
  console.log('[ResizeDetect] mouse', {
    pixel: { x: mouseX, y: mouseY },
    canvas: { x: canvasMouseX, y: canvasMouseY },
  })

  const bbox = pointsArrayToStructured(canvasPoints)
  const handles = [
    { name: 'topLeft', point: bbox.topLeft },
    { name: 'topRight', point: bbox.topRight },
    { name: 'bottomRight', point: bbox.bottomRight },
    { name: 'bottomLeft', point: bbox.bottomLeft },
  ]

  for (const handle of handles) {
    let x = handle.point.x
    let y = handle.point.y

    // Adjust handle position to match the drawing logic (handles are inside the box)
    switch (handle.name) {
      case 'topLeft':
        x += handleSizeCanvas / 2
        y += handleSizeCanvas / 2
        break
      case 'topRight':
        x -= handleSizeCanvas / 2
        y += handleSizeCanvas / 2
        break
      case 'bottomRight':
        x -= handleSizeCanvas / 2
        y -= handleSizeCanvas / 2
        break
      case 'bottomLeft':
        x += handleSizeCanvas / 2
        y -= handleSizeCanvas / 2
        break
    }

    const rect = {
      left: x - handleSizeCanvas / 2,
      right: x + handleSizeCanvas / 2,
      top: y - handleSizeCanvas / 2,
      bottom: y + handleSizeCanvas / 2,
    }
    console.log('[ResizeDetect] handle rect', { name: handle.name, rect, handleSizeCanvas })

    // Check if mouse is within the handle area (all in canvas coordinates)
    if (
      canvasMouseX >= rect.left &&
      canvasMouseX <= rect.right &&
      canvasMouseY >= rect.top &&
      canvasMouseY <= rect.bottom
    ) {
      console.log('[ResizeDetect] matched handle', handle.name)
      return handle.name
    }
  }
  console.log('[ResizeDetect] no handle matched')
  return null
}

/**
 * Hybrid drawing function that uses optimized rect drawing for axis-aligned boxes
 * and path drawing for oriented boxes
 * Points are expected to be in pixel coordinates
 */
export const drawBoundingBoxOptimized = (
  ctx: CanvasRenderingContext2D,
  box: AnnotationDataBoundingBox,
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  strokeStyle: string,
  fillStyle?: string,
  lineWidth = 2
) => {
  // Use points if available and valid (all points are in pixel coordinates)
  if (box.points && box.points.length === 8) {
    // For axis-aligned boxes, use the faster rect drawing
    if (isAxisAligned(box.points)) {
      const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(box.points)

      const scaleX = canvasWidth / imageWidth
      const scaleY = canvasHeight / imageHeight

      const x = xmin * scaleX
      const y = ymin * scaleY
      const width = (xmax - xmin) * scaleX
      const height = (ymax - ymin) * scaleY

      // Fill if fillStyle provided
      if (fillStyle) {
        ctx.fillStyle = fillStyle
        ctx.fillRect(x, y, width, height)
      }

      // Stroke
      ctx.strokeStyle = strokeStyle
      ctx.lineWidth = lineWidth
      ctx.strokeRect(x, y, width, height)
    } else {
      // Use path drawing for oriented boxes (points are in pixels)
      drawBoundingBoxFromPoints(
        ctx,
        box.points,
        imageWidth,
        imageHeight,
        canvasWidth,
        canvasHeight,
        strokeStyle,
        fillStyle,
        lineWidth
      )
    }
  } else {
    // Fallback to min/max coordinates for legacy boxes
    const scaleX = canvasWidth / imageWidth
    const scaleY = canvasHeight / imageHeight

    const x = (box.xmin ?? 0) * scaleX
    const y = (box.ymin ?? 0) * scaleY
    const width = ((box.xmax ?? 0) - (box.xmin ?? 0)) * scaleX
    const height = ((box.ymax ?? 0) - (box.ymin ?? 0)) * scaleY

    // Fill if fillStyle provided
    if (fillStyle) {
      ctx.fillStyle = fillStyle
      ctx.fillRect(x, y, width, height)
    }

    // Stroke
    ctx.strokeStyle = strokeStyle
    ctx.lineWidth = lineWidth
    ctx.strokeRect(x, y, width, height)
  }
}

const PADDING_FACTOR = 0.3 // 10% padding around the bbox when zooming

export const calculateZoomForBBox = (
  bbox: AnnotationDataBoundingBox,
  canvas: HTMLCanvasElement,
  bBoxScaleX: number,
  bBoxScaleY: number,
  imageWidth?: number,
  imageHeight?: number
): { zoomLevel: number; panOffset: MousePosition } => {
  // Use contain-fit mapping so centering works with letterboxing
  const iw = imageWidth && imageHeight ? imageWidth : 1
  const ih = imageHeight && imageWidth ? imageHeight : 1
  const fitScale = imageWidth && imageHeight ? Math.min(canvas.width / iw, canvas.height / ih) : 1
  const drawW = imageWidth && imageHeight ? iw * fitScale : canvas.width
  const drawH = imageWidth && imageHeight ? ih * fitScale : canvas.height
  const offsetX = (canvas.width - drawW) / 2
  const offsetY = (canvas.height - drawH) / 2

  // Determine bbox center and size in image pixels
  let bboxCenterX: number, bboxCenterY: number, bboxWidth: number, bboxHeight: number
  if (bbox.points && bbox.points.length === 8 && imageWidth && imageHeight) {
    const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(bbox.points)
    const centerXImg = (xmin + xmax) / 2
    const centerYImg = (ymin + ymax) / 2
    bboxWidth = (xmax - xmin) * (drawW / iw)
    bboxHeight = (ymax - ymin) * (drawH / ih)
    bboxCenterX = offsetX + centerXImg * (drawW / iw)
    bboxCenterY = offsetY + centerYImg * (drawH / ih)
  } else {
    const xmin = bbox.xmin ?? 0
    const xmax = bbox.xmax ?? 0
    const ymin = bbox.ymin ?? 0
    const ymax = bbox.ymax ?? 0
    const centerXImg = (xmin + xmax) / 2
    const centerYImg = (ymin + ymax) / 2
    bboxWidth = (xmax - xmin) * (drawW / iw)
    bboxHeight = (ymax - ymin) * (drawH / ih)
    bboxCenterX = offsetX + centerXImg * (drawW / iw)
    bboxCenterY = offsetY + centerYImg * (drawH / ih)
  }

  // Calculate zoom levels needed to fit bbox in both dimensions
  const horizontalZoom = canvas.width / (bboxWidth * (1 + 2 * PADDING_FACTOR))
  const verticalZoom = canvas.height / (bboxHeight * (1 + 2 * PADDING_FACTOR))

  // Use the smaller zoom level to ensure the entire bbox is visible
  const zoomLevel = Math.min(horizontalZoom, verticalZoom) / 2.5

  // Calculate required pan offset to center the bbox
  const panOffset = {
    x: canvas.width / 2 - bboxCenterX,
    y: canvas.height / 2 - bboxCenterY,
  }

  return { zoomLevel, panOffset }
}

export const drawAnnotations = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  zoomLevel: number,
  panOffset: MousePosition,
  filteredBoxes: AnnotationDataBoundingBox[],
  showConfidence: boolean,
  tags: Tag[],
  useBBoxes: boolean,
  showBBoxes: boolean,
  newBoundingBox: AnnotationDataBoundingBox | null,
  selectedBBox: AnnotationDataBoundingBox | null,
  mousePosition: MousePosition,
  contrast: number,
  bBoxIndex?: number,
  hoveredBoxIndex?: number,
  isMouseOverCanvas = false,
  creationAxisStart: { x: number; y: number } | null = null,
  creationPhase: 'idle' | 'axisStart' | 'axisFixed' = 'idle',
  isEditable = true,
  labelMode: 'class' | 'class+confidence' = 'class+confidence'
) => {
  // Prevent unnecessary redraws during resize
  if (ctx.canvas !== canvas) return

  // Clear the entire canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // Save the context state
  ctx.save()

  // Apply transformations only if we're in a bounding box project or if we're zooming/panning
  // if (useBBoxes || zoomLevel !== 0.85 || panOffset.x !== 0 || panOffset.y !== 0) {
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.scale(zoomLevel, zoomLevel)
  ctx.translate(-canvas.width / 2, -canvas.height / 2)
  ctx.translate(panOffset.x, panOffset.y)
  // }

  // Determine CSS scaling (canvas element may be scaled by layout)
  const rect = canvas.getBoundingClientRect()
  const cssScaleX = rect.width && canvas.width ? rect.width / canvas.width : 1
  const cssScaleY = rect.height && canvas.height ? rect.height / canvas.height : 1
  const cssScale = Math.max(cssScaleX, cssScaleY) || 1

  // Contain-fit the image inside the canvas with letterboxing offsets
  const imgW = image.naturalWidth || 1
  const imgH = image.naturalHeight || 1
  const fitScale = Math.min(canvas.width / imgW, canvas.height / imgH)
  const drawW = imgW * fitScale
  const drawH = imgH * fitScale
  const offsetX = (canvas.width - drawW) / 2
  const offsetY = (canvas.height - drawH) / 2

  // Draw image using contain fit
  ctx.drawImage(image, offsetX, offsetY, drawW, drawH)

  // Apply contrast
  if (contrast && contrast !== 0) {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const factor = (259 * (contrast + 255)) / (255 * (259 - contrast))
    const data = imageData.data
    for (let i = 0; i < data.length; i += 4) {
      data[i] = factor * (data[i] - 128) + 128 // Red
      data[i + 1] = factor * (data[i + 1] - 128) + 128 // Green
      data[i + 2] = factor * (data[i + 2] - 128) + 128 // Blue
    }
    ctx.putImageData(imageData, 0, 0)
  }

  // Add darkening overlay if there's a hovered box
  if (hoveredBoxIndex !== undefined) {
    // Canvas now matches image aspect ratio, so scaling is straightforward
    const bBoxScaleX = drawW / imgW
    const bBoxScaleY = drawH / imgH
    const hoveredBox = filteredBoxes.find((box) => box.class_index === hoveredBoxIndex)
    if (hoveredBox) {
      // First, darken the entire canvas
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Then clear the area of the hovered box to show it normally
      ctx.save()
      ctx.beginPath()
      ctx.rect(
        Math.round(offsetX + (hoveredBox.xmin ?? 0) * bBoxScaleX),
        Math.round(offsetY + (hoveredBox.ymin ?? 0) * bBoxScaleY),
        Math.round(((hoveredBox.xmax ?? 0) - (hoveredBox.xmin ?? 0)) * bBoxScaleX),
        Math.round(((hoveredBox.ymax ?? 0) - (hoveredBox.ymin ?? 0)) * bBoxScaleY)
      )
      ctx.clip()
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      // Redraw image in the clipped area
      ctx.drawImage(image, offsetX, offsetY, drawW, drawH)
      if (contrast && contrast !== 0) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const factor = (259 * (contrast + 255)) / (255 * (259 - contrast))
        const data = imageData.data
        for (let i = 0; i < data.length; i += 4) {
          data[i] = factor * (data[i] - 128) + 128
          data[i + 1] = factor * (data[i + 1] - 128) + 128
          data[i + 2] = factor * (data[i + 2] - 128) + 128
        }
        ctx.putImageData(imageData, 0, 0)
      }
      ctx.restore()
    }
  }

  if (showBBoxes) {
    const bBoxScaleX = drawW / imgW
    const bBoxScaleY = drawH / imgH

    // Draw existing bounding boxes
    const selectedBBoxIndex = filteredBoxes.findIndex((b) => {
      return b.class_index === selectedBBox?.class_index
    })
    // Translate drawing into the image draw area so (0,0) maps to image origin
    ctx.save()
    ctx.translate(offsetX, offsetY)

    filteredBoxes.forEach((box, index) => {
      if (!selectedBBox || box.class_index === selectedBBox?.class_index) {
        if (
          bBoxIndex === undefined ||
          (index === bBoxIndex && selectedBBox?.class_index === box.class_index)
        ) {
          const selectedTag = tags?.find((tag) => tag.id === box.name)

          const color = `#${
            selectedTag?.color.toString(16).padStart(6, '0').slice(0, 6) || '#80085'
          }`
          if (selectedTag) {
            // Use the new optimized drawing function that supports both axis-aligned and oriented boxes
            drawBoundingBoxOptimized(
              ctx,
              box,
              imgW,
              imgH,
              drawW,
              drawH,
              color,
              undefined, // No fill for basic outline
              2 / (zoomLevel * cssScale)
            )

            // Highlight selected or hovered box
            if (
              (selectedBBox && selectedBBoxIndex === index) ||
              box.class_index === hoveredBoxIndex
            ) {
              const fillColor = `#${selectedTag.color.toString(16).padStart(6, '0').slice(0, 6)}44`

              // Draw fill highlight using the optimized function
              drawBoundingBoxOptimized(
                ctx,
                box,
                imgW,
                imgH,
                drawW,
                drawH,
                color,
                fillColor,
                2 / (zoomLevel * cssScale)
              )

              // Draw resize handles only when editable and for selected box
              if (isEditable && selectedBBox && selectedBBoxIndex === index) {
                // Base handle size on the box's canvas size so squares/rectangles scale naturally
                const pts =
                  box.points && box.points.length === 8
                    ? box.points
                    : [
                        box.xmin ?? 0,
                        box.ymin ?? 0,
                        box.xmax ?? 0,
                        box.ymin ?? 0,
                        box.xmax ?? 0,
                        box.ymax ?? 0,
                        box.xmin ?? 0,
                        box.ymax ?? 0,
                      ]
                const xs = [pts[0], pts[2], pts[4], pts[6]]
                const ys = [pts[1], pts[3], pts[5], pts[7]]
                const minX = Math.min(...xs)
                const maxX = Math.max(...xs)
                const minY = Math.min(...ys)
                const maxY = Math.max(...ys)
                const widthCanvas = (maxX - minX) * (canvas.width / image.naturalWidth)
                const heightCanvas = (maxY - minY) * (canvas.height / image.naturalHeight)
                const minSideCanvas = Math.max(0.5, Math.min(widthCanvas, heightCanvas))
                const HANDLE_RATIO = 0.06 // 6% of shortest side
                const handleSize = minSideCanvas * HANDLE_RATIO

                // Always draw unified handle style; derive points if missing
                const handlePts = pts
                drawOrientedResizeHandles(
                  ctx,
                  handlePts,
                  imgW,
                  imgH,
                  drawW,
                  drawH,
                  // Use the same color as the bbox outline for consistency
                  color,
                  handleSize,
                  zoomLevel,
                  cssScale
                )
              }
            }
          }

          if (showConfidence && selectedBBox && selectedBBoxIndex === index) {
            // Draw confidence percentage and, if selected, append class name
            const confidence = box.confidence || 0
            const confidenceText = `${(confidence * 100).toFixed(1)}%`
            const tagName = selectedTag?.name
            const isSelected = selectedBBox && selectedBBoxIndex === index
            const displayText = (() => {
              if (!isSelected) return confidenceText
              if (!tagName) return confidenceText
              return labelMode === 'class' ? tagName : `${tagName} ${confidenceText}`
            })()

            // Setup text style
            ctx.font = `${12 / (zoomLevel * cssScale)}px Arial`
            ctx.textBaseline = 'top'

            // Create background for better readability
            const textMetrics = ctx.measureText(displayText)
            const padding = 4 / (zoomLevel * cssScale)
            const textHeight = 14 / (zoomLevel * cssScale)

            ctx.fillStyle = color
            ctx.fillRect(
              (box.xmin ?? 0) * bBoxScaleX - 1 / (zoomLevel * cssScale),
              (box.ymin ?? 0) * bBoxScaleY - textHeight - padding,
              textMetrics.width + padding * 2,
              textHeight + padding
            )

            // Draw text
            ctx.fillStyle = getContrastYIQ(color)
            ctx.fillText(
              displayText,
              (box.xmin ?? 0) * bBoxScaleX + padding / 2,
              (box.ymin ?? 0) * bBoxScaleY - textHeight - padding / 2
            )
          }
        }
      }
    })

    // Draw new bounding box if it exists
    if (newBoundingBox) {
      ctx.strokeStyle = 'blue'
      ctx.lineWidth = 2 / (zoomLevel * cssScale)
      if (newBoundingBox.points && newBoundingBox.points.length === 8) {
        // Use oriented drawing for preview when points are present
        drawBoundingBoxFromPoints(
          ctx,
          newBoundingBox.points,
          imgW,
          imgH,
          drawW,
          drawH,
          'blue',
          undefined,
          2 / (zoomLevel * cssScale)
        )
      } else {
        // Fallback to axis-aligned rect
        ctx.strokeRect(
          Math.round((newBoundingBox.xmin ?? 0) * bBoxScaleX),
          Math.round((newBoundingBox.ymin ?? 0) * bBoxScaleY),
          Math.round(((newBoundingBox.xmax ?? 0) - (newBoundingBox.xmin ?? 0)) * bBoxScaleX),
          Math.round(((newBoundingBox.ymax ?? 0) - (newBoundingBox.ymin ?? 0)) * bBoxScaleY)
        )
      }
    }

    // If in OBB axisStart phase, draw a dotted alignment line from axisStart to mouse
    if (creationAxisStart && creationPhase === 'axisStart') {
      // Convert image-space start to pre-transform canvas coordinates
      const sx = creationAxisStart.x * bBoxScaleX
      const sy = creationAxisStart.y * bBoxScaleY
      // Convert mouse (canvas pixel) to pre-transform world coordinates
      const ex =
        (mousePosition.x - canvas.width / 2) / zoomLevel + canvas.width / 2 - panOffset.x - offsetX
      const ey =
        (mousePosition.y - canvas.height / 2) / zoomLevel +
        canvas.height / 2 -
        panOffset.y -
        offsetY
      ctx.save()
      ctx.setLineDash([4 / (zoomLevel * cssScale), 4 / (zoomLevel * cssScale)])
      ctx.strokeStyle = 'rgba(0, 123, 255, 0.9)'
      ctx.lineWidth = 1.5 / (zoomLevel * cssScale)
      ctx.beginPath()
      ctx.moveTo(sx, sy)
      ctx.lineTo(ex, ey)
      ctx.stroke()
      ctx.restore()
    }

    // Restore translation for image space
    ctx.restore()
  }

  // Always restore the context state
  ctx.restore()

  // Draw crosshairs at mouse position in screen space (after restoring transformations)
  if (isMouseOverCanvas && useBBoxes) {
    const canvasX = mousePosition?.x || 0
    const canvasY = mousePosition?.y || 0

    ctx.beginPath()
    ctx.strokeStyle = 'rgba(0, 123, 255, 0.5)'
    ctx.lineWidth = 1

    // Vertical line
    ctx.moveTo(canvasX, 0)
    ctx.lineTo(canvasX, canvas.height)

    // Horizontal line
    ctx.moveTo(0, canvasY)
    ctx.lineTo(canvas.width, canvasY)

    ctx.stroke()
  }
}
